import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export interface KnowledgeBase {
  id: string;
  name: string;
  description: string | null;
  vector_store_id: string;
  status: 'creating' | 'ready' | 'error';
  file_count: number;
  created_at: string;
  updated_at: string;
  assistant_count?: number;
}

export interface KnowledgeBaseFile {
  id: string;
  knowledge_base_id: string;
  openai_file_id: string;
  filename: string;
  size_bytes: number;
  status: 'uploading' | 'processing' | 'ready' | 'error';
  error_message?: string;
  uploaded_at: string;
  deidentified?: boolean;
  phi_entities_found?: number;
  phi_entity_types?: string[];
  deidentified_at?: string;
}

interface KnowledgeBaseContextType {
  knowledgeBases: KnowledgeBase[];
  loading: boolean;
  error: string | null;
  fetchKnowledgeBases: () => Promise<void>;
  createKnowledgeBase: (name: string, description?: string) => Promise<KnowledgeBase>;
  deleteKnowledgeBase: (id: string) => Promise<void>;
  updateKnowledgeBase: (id: string, name: string, description?: string) => Promise<void>;
  uploadFilesToKB: (kbId: string, files: File[]) => Promise<void>;
  deleteFileFromKB: (kbId: string, fileId: string) => Promise<void>;
}

const KnowledgeBaseContext = createContext<KnowledgeBaseContextType>({
  knowledgeBases: [],
  loading: false,
  error: null,
  fetchKnowledgeBases: async () => {},
  createKnowledgeBase: async () => { throw new Error('Not initialized.'); },
  deleteKnowledgeBase: async () => {},
  updateKnowledgeBase: async () => {},
  uploadFilesToKB: async () => {},
  deleteFileFromKB: async () => {},
});

// Vercel serverless functions reject request bodies over ~4.5 MB. Files are
// base64-encoded (+33%), so cap raw file size to stay safely under the limit.
const MAX_FILE_BYTES = 3 * 1024 * 1024;

/** Read a File as base64 without building the string byte-by-byte on the main thread. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/** Extract a useful error message from an API response, falling back to status. */
async function readApiError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (body?.error?.message) return body.error.message;
  } catch {
    // Non-JSON body (e.g. platform 413 page) — use the fallback.
  }
  if (res.status === 413) return 'File too large to upload (server limit exceeded).';
  return fallback;
}

export function useKnowledgeBases() {
  return useContext(KnowledgeBaseContext);
}

export function KnowledgeBaseProvider({ children }: { children: ReactNode }) {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchKnowledgeBases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/knowledge-bases');
      if (res.ok) {
        const data = await res.json();
        setKnowledgeBases(data);
      } else {
        setError('Could not load knowledge bases. Please try again.');
      }
    } catch (err) {
      console.error('Failed to fetch knowledge bases:', err);
      setError('Could not load knowledge bases. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const createKnowledgeBase = useCallback(async (name: string, description?: string) => {
    const res = await fetch('/api/v1/knowledge-bases', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, description }),
    });
    if (!res.ok) throw new Error('Failed to create knowledge base.');
    const data = await res.json();
    setKnowledgeBases(prev => [data, ...prev]);
    return data;
  }, []);

  const deleteKnowledgeBase = useCallback(async (id: string) => {
    const res = await fetch(`/api/v1/knowledge-bases/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete knowledge base.');
    setKnowledgeBases(prev => prev.filter(kb => kb.id !== id));
  }, []);

  const updateKnowledgeBase = useCallback(async (id: string, name: string, description?: string) => {
    const res = await fetch(`/api/v1/knowledge-bases/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, description }),
    });
    if (!res.ok) throw new Error('Failed to update knowledge base.');
    const data = await res.json();
    setKnowledgeBases(prev => prev.map(kb => kb.id === id ? data : kb));
  }, []);

  const uploadFilesToKB = useCallback(async (kbId: string, files: File[]) => {
    // Reject files that cannot fit in a serverless request body up front,
    // with a message that says which files and why.
    const oversized = files.filter(f => f.size > MAX_FILE_BYTES);
    if (oversized.length > 0) {
      throw new Error(
        `Too large to upload (max ${(MAX_FILE_BYTES / (1024 * 1024)).toFixed(0)} MB per file): ` +
        oversized.map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(1)} MB)`).join(', ')
      );
    }

    // Upload one file per request so a single failure doesn't abort the whole
    // batch and each request stays well under the body-size limit.
    const failures: string[] = [];
    for (const f of files) {
      try {
        const base64 = await fileToBase64(f);
        const res = await fetch(`/api/v1/knowledge-bases/${kbId}/files`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ files: [{ name: f.name, data: base64, type: f.type }] }),
        });
        if (!res.ok) {
          failures.push(`${f.name}: ${await readApiError(res, 'upload failed')}`);
        }
      } catch (err) {
        failures.push(`${f.name}: ${err instanceof Error ? err.message : 'upload failed'}`);
      }
    }

    await fetchKnowledgeBases();

    if (failures.length > 0) {
      const uploadedCount = files.length - failures.length;
      throw new Error(
        (uploadedCount > 0 ? `${uploadedCount} of ${files.length} files uploaded. ` : '') +
        `Failed: ${failures.join('; ')}`
      );
    }
  }, [fetchKnowledgeBases]);

  const deleteFileFromKB = useCallback(async (kbId: string, fileId: string) => {
    const res = await fetch(`/api/v1/knowledge-bases/${kbId}/files/${fileId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete file.');
    await fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  return (
    <KnowledgeBaseContext.Provider
      value={{
        knowledgeBases,
        loading,
        error,
        fetchKnowledgeBases,
        createKnowledgeBase,
        deleteKnowledgeBase,
        updateKnowledgeBase,
        uploadFilesToKB,
        deleteFileFromKB,
      }}
    >
      {children}
    </KnowledgeBaseContext.Provider>
  );
}
